import { Component, Input, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, Subject, Subscription, takeUntil } from 'rxjs';
import { MyConfigOrganization } from '../../../../models/myConfig';
import { AuthService } from '../../../../services/auth.service';
import { BackOfficeService } from '../../../../services/backoffice.service';
import { ActivatedRoute } from '@angular/router';
import { MyConfigService } from '../../../../services/my-config.service';
import { BackOffice24StatesService } from '../state/back-office24-states.service';
import { MatTableDataSource } from '@angular/material/table';
import { KeyTaskVM } from '../../../../models/orderModel';
import { MatDialog } from '@angular/material/dialog';
import { DialogConfirmComponent } from '../../../../components/dialog-confirm/dialog-confirm.component';
import { MatSnackBar } from '@angular/material/snack-bar';

@Component({
  selector: 'app-key-tasks-list',
  standalone: false,
  templateUrl: './key-tasks-list.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./key-tasks-list.component.css']
})
export class KeyTasksListComponent implements OnInit {
  componentDestroyed$: Subject<boolean> = new Subject();
  isLoading = true;
  @Input() organization: MyConfigOrganization;
  private orgSubscription: Subscription = new Subscription();

  driverDataSource = new MatTableDataSource<KeyTaskVM>();
  adminDataSource = new MatTableDataSource<KeyTaskVM>();
  unorderedParkingsDataSource = new MatTableDataSource<any>();

  emptyData = new MatTableDataSource([{ empty: "row" }]);

  displayedColumns: string[] = [
    // 'orderLockerId',
    // 'shareboxId',
    'orderNr',
    // 'status',
    'carId',
    'safeName',
    'safeHookNo',
    'type',
    'connectedUserEmail'
    // 'safeHookStatus',
    // 'actions'
  ];

  unorderedParkingsColumns: string[] = [
    'licensePlate',
    'created',
    'parkingPeriod',
    'isOrderCreated'
  ];
  activeRequests: number = 0;

  constructor(
    public authService: AuthService,
    private BackOfficeService: BackOfficeService,
    public router: Router,
    private route: ActivatedRoute,
    private myConfigService: MyConfigService,
    public backOffice24StatesService: BackOffice24StatesService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar
  ) { }

  ngOnInit(): void {
    try {
      this.orgSubscription = this.backOffice24StatesService.currentOrganization$.subscribe(
        org => this.organization = org
      );
      this.initConfig();
    } catch (e) {
      console.log(e);
    }
  }

  private setLoadingState(isLoading: boolean) {
    this.isLoading = isLoading;
  }

  incrementRequests() {
    this.activeRequests++;
    this.setLoadingState(true);
  }

  decrementRequests() {
    if (this.activeRequests > 0)
      this.activeRequests--;
    if (this.activeRequests === 0) {
      this.setLoadingState(false);
    }
  }

  initConfig() {
    this.getOrganizationKeyTasks('driver');
    this.getUnorderedParkings();
    // this.getOrganizationKeyTasks('admin');
  }

  getUnorderedParkings() {
    this.incrementRequests();
    this.BackOfficeService.getUnorderedParkings(this.organization.organizationId)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe({
        next: (data) => {
          const filteredData = this.filterUnorderedParkings(data.items || []);
          this.unorderedParkingsDataSource.data = filteredData;
          this.decrementRequests();
        },
        error: (error) => {
          console.error('Error fetching unordered parkings:', error);
          this.decrementRequests();
        }
      });
  }

  private filterUnorderedParkings(items: any[]): any[] {
    const now = new Date();
    const swedishTime = new Date(now.toLocaleString("en-US", { timeZone: "Europe/Stockholm" }));

    return items.filter(item => {
      const createdTime = new Date(item.created);
      const timeDifferenceMs = swedishTime.getTime() - createdTime.getTime();
      const timeDifferenceMinutes = Math.floor(timeDifferenceMs / (1000 * 60));
      item.parkingPeriod = this.formatParkingPeriod(Math.abs(timeDifferenceMinutes));
      return timeDifferenceMinutes >= 15;
    });
  }

  private formatParkingPeriod(minutes: number): string {
    if (minutes < 60) {
      return `${minutes} min`;
    } else if (minutes < 1440) {
      const hours = Math.floor(minutes / 60);
      return hours === 1 ? '1 hr' : `${hours} hrs`;
    } else {
      const days = Math.floor(minutes / 1440);
      return days === 1 ? '1 day' : `${days} days`;
    }
  }

  getOrganizationKeyTasks(role: 'driver' | 'admin') {
    this.incrementRequests();
    this.BackOfficeService.getKeyTasks(this.organization.organizationId, role)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe({
        next: (data) => {
          if (role === 'driver') {
            this.driverDataSource.data = data;
          } else if (role === 'admin') {
            this.adminDataSource.data = data;
          }
          this.decrementRequests();
        },
        error: (error) => {
          console.error('Error fetching key tasks:', error);
          this.decrementRequests();
        }
      });
  }

  getStatusData(type: string, value: number): { text: string; color: string } {
    const statusMaps = {
      'type': {
        map: {
          1: {
            text: 'KEY_TASKS.TYPE_LABELS.1',
            color: '#3f51b5'
          },
          2: {
            text: 'KEY_TASKS.TYPE_LABELS.2',
            color: '#3f51b5'
          },
          3: {
            text: 'KEY_TASKS.TYPE_LABELS.3',
            color: '#3f51b5'
          }
        },
        default: {
          text: 'KEY_TASKS.TYPE_LABELS.DEFAULT',
          color: '#000000'
        }
      },
      'status': {
        map: {
          0: {
            text: 'KEY_TASKS.STATUS_LABELS.0',
            color: '#28a745'
          },
          1: {
            text: 'KEY_TASKS.STATUS_LABELS.1',
            color: '#3f51b5'
          },
          2: {
            text: 'KEY_TASKS.STATUS_LABELS.2',
            color: '#dc3545'
          }
        },
        default: {
          text: 'KEY_TASKS.STATUS_LABELS.DEFAULT',
          color: '#000000'
        }
      },
      'safeHook': {
        map: {
          0: {
            text: 'KEY_TASKS.SAFE_HOOK_STATUS_LABELS.0',
            color: '#28a745'
          },
          1: {
            text: 'KEY_TASKS.SAFE_HOOK_STATUS_LABELS.1',
            color: '#3f51b5'
          },
          2: {
            text: 'KEY_TASKS.SAFE_HOOK_STATUS_LABELS.2',
            color: '#dc3545'
          }
        },
        default: {
          text: 'KEY_TASKS.SAFE_HOOK_STATUS_LABELS.DEFAULT',
          color: '#000000'
        }
      }
    };

    const map = statusMaps[type].map;
    return map[value] || statusMaps[type].default;
  }

  ngOnDestroy() {
    this.componentDestroyed$.next(true);
    this.componentDestroyed$.complete();
    this.orgSubscription.unsubscribe();
  }

  onRowClick(element: KeyTaskVM): void {
    if (element.orderId != null) {
    const url = this.router.serializeUrl(this.router.createUrlTree([`/admin/back-office24-order/${element.orderId}`]));
    window.open(url, '_blank');    }
  }

  goToOrder(orderId: string): void {
    const url = this.router.serializeUrl(this.router.createUrlTree([`/admin/back-office24-order/${orderId}`]));
    window.open(url, '_blank');
  }

  completeTask(taskId: string, role: 'key-driver' | 'driver' | 'admin'): void {
    const dialogRef = this.dialog.open(DialogConfirmComponent, {
      width: '400px',
      data: {
        title: 'KEY_TASKS.CONFIRM_COMPLETE_TASK',
        message: 'KEY_TASKS.CONFIRM_COMPLETE_TASK_MESSAGE',
        confirmText: 'FORM.CONFIRM',
        cancelText: 'FORM.CANCEL'
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        let completeTask$: Observable<boolean>;
        if (role === 'key-driver') {
          completeTask$ = this.BackOfficeService.markKeyTakenFromCustomerDone(this.organization.organizationId, taskId);
        } else if (role === 'driver') {
          completeTask$ = this.BackOfficeService.markDriverTaskDone(this.organization.organizationId, taskId);
        } else if (role === 'admin') {
          completeTask$ = this.BackOfficeService.markAdminTaskDone(this.organization.organizationId, taskId);
        }

        completeTask$
          .pipe(takeUntil(this.componentDestroyed$))
          .subscribe({
            next: (result) => {
              if (result) {
                // Refresh both tables after completion
                this.getOrganizationKeyTasks('driver');
                this.getOrganizationKeyTasks('admin');
                this.snackBar.open('Task completed successfully', 'Close', {
                  duration: 5000,
                  verticalPosition: 'top',
                  horizontalPosition: 'center',
                  panelClass: ['success-snackbar']
                });
              } else {
                this.snackBar.open('Failed to complete task. Please try again.', 'Close', {
                  duration: 5000,
                  verticalPosition: 'top',
                  horizontalPosition: 'center',
                  panelClass: ['error-snackbar']
                });
              }
            },
            error: (error) => {
              this.snackBar.open('An error occurred while completing the task. Please try again.', 'Close', {
                duration: 5000,
                verticalPosition: 'top',
                horizontalPosition: 'center',
                panelClass: ['error-snackbar']
              });
            }
          });
      }
    });
  }
}
